/**
 * Plain-language copy strings for the Medical Congress Solution Platform app
 * All messages use simple, clear language accessible to all users
 */

export const copy = {
  // Selection flow
  selectAsset: 'Select asset',
  selectIndication: 'Select indication',
  selectIndicationHelper: 'Select an asset first',
  selectKiqs: 'Key Insight Topics',
  selectKiqsHelper: 'Select an indication first',
  selectKiqsRequired: 'Select at least one question to continue',
  
  // Recording
  startRecording: 'Start recording',
  stopRecording: 'Stop recording',
  pauseRecording: 'Pause recording',
  resumeRecording: 'Resume recording',
  recordingInProgress: 'Recording in progress',
  recordingSaved: 'Recording saved as draft',
  recordingFailed: 'Failed to save recording. Please try again.',
  recordingComplete: 'Recording complete',
  
  // Waveform
  waveformReady: 'Waveform will appear when recording starts',
  waveformDisplaying: 'Recording in progress - waveform displaying audio levels',
  
  // Notes
  noteInputLabel: 'Type a Note',
  noteInputPlaceholder: 'Enter your note here... (Cmd/Ctrl + Enter to send)',
  noteSendButton: 'Send Note',
  noteSaved: 'Note saved successfully',
  noteFailed: 'Failed to save note. Please try again.',
  noteValidationFailed: 'Note validation failed',
  noteEmpty: 'Please enter a note before sending',
  noteKeyboardShortcut: 'Use Cmd/Ctrl + Enter to send quickly',
  
  // Validation
  completeSelections: 'Please complete all selections before recording',
  completeSelectionsForNote: 'Please complete all selections before sending',
  
  // Recent insights
  recentInsightsTitle: 'Recent Insights',
  loadingRecordings: 'Loading recordings...',
  noRecordings: 'No recordings or notes yet',
  noRecordingsSubtext: 'Record audio or type a note to see it here',
  
  // Playback
  playRecording: 'Play recording',
  pausePlayback: 'Pause recording',
  stopPlayback: 'Stop recording',
  deleteRecording: 'Delete recording',
  deleteNote: 'Delete note',
  viewFullNote: 'View full note',
  playbackOfflineRemote: 'Connect to the internet to play this saved recording.',
  
  // Clear data
  clearDataButton: 'Clear Device Data',
  clearDataConfirmTitle: 'Clear All Data?',
  clearDataConfirmMessage: 'This will permanently delete all recordings, notes, and settings stored on this device. This action cannot be undone.',
  clearDataConfirm: 'Yes, Clear All',
  clearDataCancel: 'Cancel',
  clearDataSuccess: 'All data cleared successfully',
  clearDataFailed: 'Failed to clear data. Please try again.',
  
  // Install prompt
  installAppTitle: 'Install App',
  installAppMessage: 'Install this app on your device for quick access and offline use. Works great on mobile!',
  installButton: 'Install',
  installDismiss: 'Not Now',
  installClose: 'Close install prompt',
  
  // Offline
  offlineMode: 'Offline Mode:',
  offlineMessage: 'App works offline. All recordings and data are saved locally.',
  connectionRestored: 'Connection restored',
  workingOffline: 'Working offline. All data saved locally.',
  
  // Microphone
  micPermissionRequired: 'Microphone Access Required',
  micPermissionDenied: 'Microphone permission denied. Please enable microphone access.',
  micRetry: 'Retry',
  micNotFound: 'No microphone found. Please connect a microphone.',
  
  // Errors
  errorGeneric: 'Something went wrong. Please try again.',
  errorValidation: 'Please check your input and try again.',

  // Upload / sync status (kept intentionally short and non-technical)
  statusWaitingToUpload: 'Waiting to upload',
  statusUploading: 'Uploading…',
  statusUploaded: 'Uploaded',
  statusUploadDidNotFinish: 'Upload didn\u2019t finish',
  statusKeepAppOpen: 'Please keep the app open',
  statusAllUploaded: 'All recordings uploaded',
  statusRecordingsSafeLocally: 'Your recordings are safe on this device until they upload.',
  statusWaitingForConnection: 'Waiting for connection — saved on this device',
  syncStarted: 'Syncing recordings…',
  syncFailedShort: 'Sync failed — please try again.',
  syncAllAlreadySynced: 'All recordings are already synced',
  submissionWindowClosed: 'This asset is no longer accepting recordings.',

  // Empty states
  noAssetsAvailable: 'No assets available',
  noIndicationsAvailable: 'No indications available',
  noKiqsAvailable: 'No questions available for this indication',
} as const

export type CopyKey = keyof typeof copy

/**
 * Count-aware status helpers. Kept as standalone functions so `copy` stays a
 * flat string map (single source of truth for static copy).
 */
const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  count === 1 ? singular : plural

export const statusNWaitingToUpload = (count: number) =>
  `${count} ${pluralize(count, 'recording')} waiting to upload`

export const statusNDidNotFinish = (count: number) =>
  `${count} ${pluralize(count, 'recording')} didn\u2019t finish uploading`

export const syncSuccessMessage = (count: number) =>
  `Synced ${count} ${pluralize(count, 'recording')}`

